import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import { closeLoan, createLoan, decideLoan, loadDirectory, loadLoans, type LoanRow } from "../data";
import { money } from "../lib/payslip";
import type { DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const nextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
export const loanTone: Record<LoanRow["status"], string> = { requested: "warning", active: "info", rejected: "danger", cancelled: "neutral" };

/** Loans and salary advances: requests to decide, balances and new loans. Interest-free, at most 10% of pay a month. */
export function Loans({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick, lang } = useI18n();
  const cid = ws.membership.company_id;
  const canManage = ws.permissions.has("loans.manage");
  const [rows, setRows] = useState<LoanRow[] | null>(null);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [deciding, setDeciding] = useState<Record<string, { month: string; n: string; note: string }>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => loadLoans(cid).then(setRows).catch((e) => setMsg({ ok: false, text: e.message }));
  useEffect(() => { void refresh(); void loadDirectory(cid).then(setPeople); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = (id: string) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const month = (m: string | null) => (m ? new Date(`${m}T00:00:00`).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { month: "short", year: "numeric" }) : "—");
  const requests = (rows ?? []).filter((r) => r.status === "requested");
  const active = (rows ?? []).filter((r) => r.status === "active");
  const past = (rows ?? []).filter((r) => r.status === "rejected" || r.status === "cancelled");
  const outstanding = active.reduce((a, r) => a + Number(r.outstanding), 0);

  async function act(fn: () => Promise<unknown>, done: StringKey) {
    setMsg(null);
    try { await fn(); await refresh(); setMsg({ ok: true, text: t(done) }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const d = (id: string, r: LoanRow) => deciding[id] ?? { month: nextMonth(), n: String(r.installments), note: "" };

  return (
    <div className="page">
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="kpis">
        <div className="stat"><span className="stat-label">{t("loanActive")}</span><span className="stat-value">{active.length}</span></div>
        <div className="stat"><span className="stat-label">{t("loanOutstanding")}</span><span className="stat-value small mono">{money(outstanding)}</span></div>
        <div className="stat"><span className="stat-label">{t("loanRequests")}</span><span className="stat-value">{requests.length}</span></div>
      </div>

      {requests.length > 0 && (
        <section className="card">
          <h2>{t("loanRequests")}</h2>
          <ul className="approval-list">
            {requests.map((r) => {
              const x = d(r.loan_id, r);
              return (
                <li key={r.loan_id} className="approval">
                  <div className="approval-main">
                    <button type="button" className="link strong" onClick={() => onOpen(r.employment_id)}>{name(r.employment_id)}</button>
                    <span>{t(`loanKind_${r.kind}` as StringKey)} · <span className="mono">{money(r.principal)}</span> · {r.installments} {t("loanMonths")}</span>
                    {r.reason && <span className="muted small-note">“{r.reason}”</span>}
                  </div>
                  {canManage && (
                    <div className="approval-actions">
                      <div className="row">
                        <input type="month" aria-label={t("loanFirstMonth")} value={x.month} onChange={(e) => setDeciding({ ...deciding, [r.loan_id]: { ...x, month: e.target.value } })} />
                        <input type="number" min={1} max={60} aria-label={t("loanInstallments")} style={{ width: 80 }} value={x.n} onChange={(e) => setDeciding({ ...deciding, [r.loan_id]: { ...x, n: e.target.value } })} />
                      </div>
                      <input placeholder={t("decisionNote")} aria-label={t("decisionNote")} value={x.note} onChange={(e) => setDeciding({ ...deciding, [r.loan_id]: { ...x, note: e.target.value } })} />
                      <div className="row">
                        <button type="button" className="btn primary small" onClick={() => act(() => decideLoan(r.loan_id, true, `${x.month}-01`, Number(x.n), x.note), "loanApproved")}>{t("approve")}</button>
                        <button type="button" className="btn secondary small" onClick={() => act(() => decideLoan(r.loan_id, false, null, null, x.note), "loanRejected")}>{t("reject")}</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="card">
        <h2>{t("loanActive")}</h2>
        {rows === null ? <p className="muted">{t("loading")}</p> : active.length === 0 ? <p className="muted">{t("loanNone")}</p> : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t("employee")}</th><th>{t("payAmount")}</th><th>{t("loanRecovered")}</th><th>{t("loanLeft")}</th><th className="hide-sm">{t("loanNext")}</th><th /></tr></thead>
            <tbody>{active.map((r) => {
              const p = people.find((x) => x.employment_id === r.employment_id);
              return (
                <tr key={r.loan_id}>
                  <td><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{name(r.employment_id)}</button><br />
                    <span className="muted small-note">{t(`loanKind_${r.kind}` as StringKey)} · {r.installments} × {money(r.principal / r.installments)}{r.reason ? ` · ${r.reason}` : ""}</span>
                    {p?.status === "terminated" && Number(r.outstanding) > 0 && <span className="badge danger"> {t("loanLeaver")}</span>}</td>
                  <td className="mono">{money(r.principal)}</td>
                  <td className="mono">{money(r.recovered)}</td>
                  <td className="mono strong">{money(r.outstanding)}</td>
                  <td className="hide-sm">{r.next_due ? <>{month(r.next_due)} · <span className="mono">{money(r.next_amount ?? 0)}</span></> : t("loanDone")}</td>
                  <td className="cell-actions">{canManage && Number(r.outstanding) > 0 && (
                    <button type="button" className="btn secondary small" onClick={() => {
                      const note = window.prompt(t("loanCloseAsk")) ?? null;
                      if (note !== null) void act(() => closeLoan(r.loan_id, note), "loanClosed");
                    }}>{t("loanClose")}</button>
                  )}</td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
        <p className="muted small-note">{t("loanRule")}</p>
      </section>

      {canManage && <NewLoan people={people} onDone={refresh} />}

      {past.length > 0 && (
        <details className="card">
          <summary>{t("loanPast")} ({past.length})</summary>
          <ul className="req-list">
            {past.map((r) => (
              <li key={r.loan_id}><span>{name(r.employment_id)} · {t(`loanKind_${r.kind}` as StringKey)} · <span className="mono">{money(r.principal)}</span>{r.reason ? ` · ${r.reason}` : ""}</span>
                <span className={`badge ${loanTone[r.status]}`}>{t(`loanStatus_${r.status}` as StringKey)}</span></li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function NewLoan({ people, onDone }: { people: DirectoryRow[]; onDone: () => void }) {
  const { t, pick } = useI18n();
  const [f, setF] = useState({ emp: "", kind: "loan" as "loan" | "advance", amount: "", n: "6", month: nextMonth(), note: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await createLoan(f.emp, f.kind, Number(f.amount), Number(f.n), `${f.month}-01`, f.note);
      setMsg({ ok: true, text: t("loanGiven") });
      setF({ ...f, emp: "", amount: "", note: "" });
      onDone();
    } catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  return (
    <form className="card" onSubmit={save}>
      <h2>{t("loanNew")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("employee")}</span>
          <select value={f.emp} onChange={(e) => setF({ ...f, emp: e.target.value })} required>
            <option value="">—</option>
            {people.filter((p) => p.status === "active" || p.status === "probation").map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)}</option>)}
          </select></label>
        <label className="field"><span>{t("otKind")}</span>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "loan" | "advance" })}>
            <option value="loan">{t("loanKind_loan")}</option><option value="advance">{t("loanKind_advance")}</option>
          </select></label>
        <label className="field"><span>{t("payAmount")}</span><input type="number" min="1" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required /></label>
        <label className="field"><span>{t("loanInstallments")}</span><input type="number" min="1" max="60" value={f.n} onChange={(e) => setF({ ...f, n: e.target.value })} required /></label>
        <label className="field"><span>{t("loanFirstMonth")}</span><input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} required /></label>
        <label className="field"><span>{t("note")}</span><input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
      </div>
      {f.amount && Number(f.n) > 0 && <p className="muted small-note">{t("loanEach")}: <span className="mono">{money(Number(f.amount) / Number(f.n))}</span></p>}
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="actions"><button className="btn primary">{t("loanGive")}</button></div>
    </form>
  );
}
